import Phaser from 'phaser';
import PauseOverlay from '../PauseOverlay.js';
import PauseManager from '../PauseManager.js';

// ===== PLATFORM TYPES ENUM =====
const PLATFORM_TYPES = {
    SOLID: 'solid',           // Full collision (current behavior)
    ONE_WAY: 'one_way',       // Can jump through from below, land on from above
    BREAKABLE: 'breakable'    // Can be broken/destroyed
};

// ===== PLATFORM MANAGER CLASS =====
class PlatformManager {
    constructor(scene) {
        this.scene = scene;
        this.solidPlatforms = scene.physics.add.staticGroup();
        this.oneWayPlatforms = scene.physics.add.staticGroup();
        this.breakablePlatforms = scene.physics.add.staticGroup();
        
        this.setupCollisions();
    }
    
    setupCollisions() {
        // Solid platforms - normal collision
        this.scene.physics.add.collider(this.scene.player, this.solidPlatforms);
        
        // One-way platforms - special collision (only from above)
        this.scene.physics.add.collider(this.scene.player, this.oneWayPlatforms, null, this.oneWayCollisionCheck.bind(this));
        
        // Breakable platforms - normal collision but can be broken
        this.scene.physics.add.collider(this.scene.player, this.breakablePlatforms, this.handleBreakablePlatform.bind(this));
    }
    
    setupEnemyCollisions() {
        // Set up enemy collisions after enemies are created
        if (this.scene.enemies) {
            this.scene.physics.add.collider(this.scene.enemies, this.solidPlatforms);
            this.scene.physics.add.collider(this.scene.enemies, this.oneWayPlatforms, null, this.oneWayCollisionCheck.bind(this));
            this.scene.physics.add.collider(this.scene.enemies, this.breakablePlatforms);
        }
    }
    
    setupCollectibleCollisions() {
        // Set up collectible collisions after collectibles are created
        if (this.scene.collectibles) {
            this.scene.physics.add.collider(this.scene.collectibles, this.solidPlatforms);
            this.scene.physics.add.collider(this.scene.collectibles, this.oneWayPlatforms);
            this.scene.physics.add.collider(this.scene.collectibles, this.breakablePlatforms);
        }
    }
    
    oneWayCollisionCheck(player, platform) {
        // If player is intentionally falling through, skip collision
        if (this.scene.fallingThroughOneWay) {
            return false;
        }
        
        // Only allow collision if player is falling and coming from above
        return player.body.velocity.y > 0 && player.y < platform.y;
    }
    
    handleBreakablePlatform(player, platform) {
        // If player is dashing, instantly break the platform
        if (this.scene.dash && this.scene.dash.isDashing) {
            this.breakPlatform(platform);
            return false; // Don't stop the player
        }
        
        // Check if player is performing a super jump or falling fast
        const breakThreshold = 400; // Velocity threshold to break platform
        
        if (Math.abs(player.body.velocity.y) > breakThreshold || 
            Math.abs(player.body.velocity.x) > breakThreshold) {
            this.breakPlatform(platform);
            return false; // Don't stop the player
        }
        return true; // Normal collision
    }
    
    breakPlatform(platform) {
        // Visual breaking effect
        platform.setTint(0xFF0000);
        platform.setAlpha(0.5);
        
        // Create particle effect - more intense if broken by dash
        const isDashBreak = this.scene.dash && this.scene.dash.isDashing;
        this.scene.add.particles(platform.x, platform.y, 'platform_breakable', {
            quantity: isDashBreak ? 15 : 8,
            scale: { start: isDashBreak ? 0.5 : 0.3, end: 0 },
            speed: { min: isDashBreak ? 80 : 50, max: isDashBreak ? 250 : 150 },
            lifespan: isDashBreak ? 800 : 500
        });
        
        // Show breaking notification - different message for dash
        if (isDashBreak) {
            this.scene.showDOMNotification('Platform Smashed by Dash!', 1200, {
                top: '25%',
                color: '#00FFFF',
                fontSize: '16px',
                backgroundColor: 'rgba(0, 255, 255, 0.3)'
            });
            
            // Add bonus points for dash breaking
            this.scene.score += 25;
            this.scene.updateDOMHUD();
        } else {
            this.scene.showDOMNotification('Platform Broken!', 1000, {
                top: '25%',
                color: '#FF4444',
                fontSize: '16px'
            });
        }
        
        // Remove platform after animation
        this.scene.time.delayedCall(200, () => {
            platform.destroy();
        });
        
        // Respawn platform after 5 seconds
        this.scene.time.delayedCall(5000, () => {
            this.createPlatform(
                platform.x, platform.y, 
                platform.displayWidth, platform.displayHeight,
                PLATFORM_TYPES.BREAKABLE,
                platform.tintTopLeft
            );
        });
    }
    
    createPlatform(x, y, width, height, type = PLATFORM_TYPES.SOLID, color = 0x32CD32) {
        let platform;
        let textureKey;
        
        // Select texture based on platform type
        switch (type) {
            case PLATFORM_TYPES.SOLID:
                textureKey = 'platform_solid'; // Green
                platform = this.solidPlatforms.create(x, y, textureKey)
                    .setScale(width / 32, height / 32)
                    .refreshBody();
                break;
                
            case PLATFORM_TYPES.ONE_WAY:
                textureKey = 'platform_oneway'; // Yellow ochre
                platform = this.oneWayPlatforms.create(x, y, textureKey)
                    .setScale(width / 32, height / 32)
                    .refreshBody();
                // Add visual indicator for one-way platforms
                platform.setAlpha(0.8);
                break;
                
            case PLATFORM_TYPES.BREAKABLE:
                textureKey = 'platform_breakable'; // Brown
                platform = this.breakablePlatforms.create(x, y, textureKey)
                    .setScale(width / 32, height / 32)
                    .refreshBody();
                // Add visual indicator for breakable platforms
                platform.setAlpha(0.9);
                break;
        }
        
        // Store platform type for reference
        platform.platformType = type;
        return platform;
    }
    
    getAllPlatforms() {
        return [
            ...this.solidPlatforms.children.entries,
            ...this.oneWayPlatforms.children.entries,
            ...this.breakablePlatforms.children.entries
        ];
    }
}

export default class GameScene extends Phaser.Scene {
    constructor() {
        super({ key: 'GameScene' });
    }

    preload() {
        // Create colored textures programmatically to ensure proper colors
        this.createColoredTextures();
        this.createBlackHoleTextures();
    }

    createColoredTextures() {
        // Create a white base texture for tinting
        const graphics = this.add.graphics();
        
        // Create solid white rectangle for platforms
        graphics.fillStyle(0xFFFFFF);
        graphics.fillRect(0, 0, 32, 32);
        graphics.generateTexture('platform_base', 32, 32);
        
        // Create solid white rectangle for player
        graphics.clear();
        graphics.fillStyle(0xFFFFFF);
        graphics.fillRect(0, 0, 32, 48);
        graphics.generateTexture('player_base', 32, 48);
        
        // Create solid white rectangle for enemies
        graphics.clear();
        graphics.fillStyle(0xFFFFFF);
        graphics.fillRect(0, 0, 24, 24);
        graphics.generateTexture('enemy_base', 24, 24);
        
        // Create specific colored textures to avoid tinting issues
        
        // RED ENEMY texture
        graphics.clear();
        graphics.fillStyle(0xFF0000); // Bright red
        graphics.fillRect(0, 0, 24, 24);
        graphics.generateTexture('enemy_red', 24, 24);
        
        // YELLOW OCHRE ONE-WAY PLATFORM texture
        graphics.clear();
        graphics.fillStyle(0xDAA520); // Yellow ochre
        graphics.fillRect(0, 0, 32, 32);
        graphics.generateTexture('platform_oneway', 32, 32);
        
        // GREEN SOLID PLATFORM texture
        graphics.clear();
        graphics.fillStyle(0x228B22); // Forest green
        graphics.fillRect(0, 0, 32, 32);
        graphics.generateTexture('platform_solid', 32, 32);
        
        // BROWN BREAKABLE PLATFORM texture
        graphics.clear();
        graphics.fillStyle(0x8B4513); // Saddle brown
        graphics.fillRect(0, 0, 32, 32);
        graphics.generateTexture('platform_breakable', 32, 32);
        
        // PINK PLAYER texture
        graphics.clear();
        graphics.fillStyle(0xFF6B6B); // Light red/pink
        graphics.fillRect(0, 0, 32, 48);
        graphics.generateTexture('player_normal', 32, 48);
        
        // ORANGE CROUCHING PLAYER texture
        graphics.clear();
        graphics.fillStyle(0xFFAA00); // Orange
        graphics.fillRect(0, 0, 32, 24);
        graphics.generateTexture('player_crouch', 32, 24);
        
        // Destroy the graphics object
        graphics.destroy();
    }

    createBlackHoleTextures() {
        const graphics = this.add.graphics();
        
        // Create black hole center (pure black)
        graphics.fillStyle(0x000000); // Pure black
        graphics.fillCircle(16, 16, 16);
        graphics.generateTexture('blackhole_center', 32, 32);
        
        // Create black hole rim (dark purple)
        graphics.clear();
        graphics.fillStyle(0x2E0854); // Dark purple
        graphics.fillCircle(20, 20, 20);
        graphics.fillStyle(0x000000); // Black center
        graphics.fillCircle(20, 20, 16);
        graphics.generateTexture('blackhole_rim', 40, 40);
        
        // Create particle texture for black hole effect
        graphics.clear();
        graphics.fillStyle(0x4A0E4E); // Purple particle
        graphics.fillCircle(4, 4, 4);
        graphics.generateTexture('blackhole_particle', 8, 8);
        
        graphics.destroy();
    }

    createCollectibleTextures() {
        const graphics = this.add.graphics();
        
        // GOLD collectible
        graphics.fillStyle(0xFFD700); // Gold
        graphics.fillRect(0, 0, 16, 16);
        graphics.generateTexture('collectible_gold', 16, 16);
        
        // ORANGE collectible
        graphics.clear();
        graphics.fillStyle(0xFFA500); // Orange
        graphics.fillRect(0, 0, 16, 16);
        graphics.generateTexture('collectible_orange', 16, 16);
        
        // YELLOW collectible
        graphics.clear();
        graphics.fillStyle(0xFFFF00); // Yellow
        graphics.fillRect(0, 0, 16, 16);
        graphics.generateTexture('collectible_yellow', 16, 16);
        
        // LIGHT YELLOW collectible
        graphics.clear();
        graphics.fillStyle(0xFFF700); // Light yellow
        graphics.fillRect(0, 0, 16, 16);
        graphics.generateTexture('collectible_lightyellow', 16, 16);
        
        // RED ORANGE collectible
        graphics.clear();
        graphics.fillStyle(0xFF4500); // Red orange
        graphics.fillRect(0, 0, 20, 20);
        graphics.generateTexture('collectible_redorange', 20, 20);
        
        graphics.destroy();
    }

    create() {
        // Set up pause system with Enter/Return key and gamepad start button
        this.isPaused = false;
        this.pauseManager = new PauseManager({
            getPaused: () => this.isPaused,
            setPaused: (paused) => {
                this.isPaused = paused;
            },
            onPause: () => {
                console.log('[GameScene] ⏸️ GAME PAUSED');
                this.scene.pause();
                this.createPauseOverlay();
            },
            onResume: () => {
                console.log('[GameScene] ▶️ GAME RESUMED');
                this.scene.resume();
                this.removePauseOverlay();
            }
        });

        this.pauseOverlayManager = new PauseOverlay();

        const returnKey = this.input.keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.ENTER);
        returnKey.on('down', () => {
            this.toggleGamePause();
        });

        // Set a light blue background color
        this.cameras.main.setBackgroundColor('#87CEEB'); // Sky blue
        
        // Mobile orientation detection
        this.isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
        this.isPortrait = window.innerHeight > window.innerWidth;
        
        // Create player first - start in Section 3 (top-left)
        this.player = this.physics.add.sprite(100, 450, 'player_normal');
        this.player.setBounce(0.2);
        // REMOVED setCollideWorldBounds - allows free movement across expanded world
        // this.player.setCollideWorldBounds(true);

        // Create enemies distributed across all 4 sections
        this.enemies = this.physics.add.group();
        
        // Section 1 enemies (bottom-left) - FREE ROAMING - BRIGHT RED
        const enemy1 = this.enemies.create(400, 1090, 'enemy_red');
        enemy1.setBounce(1);
        enemy1.setVelocity(Phaser.Math.Between(-200, 200), 20);
        // REMOVED setCollideWorldBounds for free movement

        // Section 2 enemies (bottom-right) - FREE ROAMING - BRIGHT RED
        const enemy2 = this.enemies.create(1400, 1090, 'enemy_red');
        enemy2.setBounce(1);
        enemy2.setVelocity(Phaser.Math.Between(-150, 150), 20);
        // REMOVED setCollideWorldBounds for free movement

        // Section 3 enemies (top-left) - FREE ROAMING - BRIGHT RED
        const enemy3 = this.enemies.create(750, 250, 'enemy_red');
        enemy3.setBounce(1);
        enemy3.setVelocity(Phaser.Math.Between(-180, 180), 20);
        // REMOVED setCollideWorldBounds for free movement

        // Close enemy near player start for immediate visibility - BRIGHT RED
        const enemyClose = this.enemies.create(300, 400, 'enemy_red');
        enemyClose.setBounce(1);
        enemyClose.setVelocity(Phaser.Math.Between(-100, 100), 20);

        // Section 4 enemies (top-right) - FREE ROAMING - BRIGHT RED
        const enemy4 = this.enemies.create(1750, 250, 'enemy_red');
        enemy4.setBounce(1);
        enemy4.setVelocity(Phaser.Math.Between(-160, 160), 20);
        // REMOVED setCollideWorldBounds for free movement
        
        // Additional roaming enemies across the world - FREE ROAMING - BRIGHT RED
        const enemy5 = this.enemies.create(1024, 750, 'enemy_red');
        enemy5.setBounce(1);
        enemy5.setVelocity(Phaser.Math.Between(-120, 120), 20);
        // REMOVED setCollideWorldBounds for free movement

        // Initialize Platform Manager System (handles all platform types and collisions)
        this.platformManager = new PlatformManager(this);
        
        // Create each room with its own method for easy editing
        this.createRoom1(); // Bottom-Left (0-1024, 640-1280)
        this.createRoom2(); // Bottom-Right (1024-2048, 640-1280) 
        this.createRoom3(); // Top-Left (0-1024, 0-640)
        this.createRoom4(); // Top-Right (1024-2048, 0-640)
        
        // Create connections between rooms
        this.createRoomConnections();
        
        // Set up enemy collisions with all platform types
        this.platformManager.setupEnemyCollisions();
        
        this.physics.add.collider(this.player, this.enemies, this.hitEnemy, null, this);

        // Create collectibles distributed across all 4 sections
        this.collectibles = this.physics.add.group();
        
        // First create collectible textures
        this.createCollectibleTextures();
        
        // Section 1 collectibles (bottom-left) - GOLD
        for (let i = 0; i < 8; i++) {
            const collectible = this.collectibles.create(50 + i * 120, 1000, 'collectible_gold');
            collectible.setDisplaySize(16, 16);
            collectible.setBounceY(Phaser.Math.FloatBetween(0.4, 0.8));
        }
        
        // Section 2 collectibles (bottom-right) - ORANGE
        for (let i = 0; i < 8; i++) {
            const collectible = this.collectibles.create(1074 + i * 120, 1000, 'collectible_orange');
            collectible.setDisplaySize(16, 16);
            collectible.setBounceY(Phaser.Math.FloatBetween(0.4, 0.8));
        }
        
        // Section 3 collectibles (top-left) - YELLOW
        for (let i = 0; i < 8; i++) {
            const collectible = this.collectibles.create(50 + i * 120, 360, 'collectible_yellow');
            collectible.setDisplaySize(16, 16);
            collectible.setBounceY(Phaser.Math.FloatBetween(0.4, 0.8));
        }
        
        // Section 4 collectibles (top-right) - LIGHT YELLOW
        for (let i = 0; i < 8; i++) {
            const collectible = this.collectibles.create(1074 + i * 120, 360, 'collectible_lightyellow');
            collectible.setDisplaySize(16, 16);
            collectible.setBounceY(Phaser.Math.FloatBetween(0.4, 0.8));
        }
        
        // Bonus collectibles on connecting platforms - RED ORANGE
        const bonusCollectible1 = this.collectibles.create(512, 750, 'collectible_redorange');
        bonusCollectible1.setDisplaySize(20, 20);
        bonusCollectible1.setBounceY(0.6);
        
        const bonusCollectible2 = this.collectibles.create(1536, 750, 'collectible_redorange');
        bonusCollectible2.setDisplaySize(20, 20);
        bonusCollectible2.setBounceY(0.6);

        // Set up collectible collisions with all platform types  
        this.platformManager.setupCollectibleCollisions();
        
        this.physics.add.overlap(this.player, this.collectibles, this.collectItem, null, this);

        // Input controls - SAD + J for jump + K for dash + E for exit + W available
        this.cursors = this.input.keyboard.addKeys('W,S,A,D,J,K,E');
        
        // Additional arrow keys for alternative controls
        this.arrowKeys = this.input.keyboard.createCursorKeys();

        // Score and Lives - now using DOM elements instead of Phaser text
        this.score = 0;
        this.lives = 3;
        this.createDOMHUD();

        // Game state
        this.gameOver = false;
        
        // Initialize Dash System
        this.initDashSystem();
        
        // Initialize Crouch System
        this.initCrouchSystem();
        
        // Initialize Black Hole System
        this.initBlackHoleSystem();
        
        // Setup orientation-based camera and gameplay
        this.setupOrientationGameplay();
        
        // Listen for orientation changes
        window.addEventListener('orientationchange', () => {
            setTimeout(() => {
                this.handleOrientationChange();
            }, 100);
        });
        
        window.addEventListener('resize', () => {
            setTimeout(() => {
                this.handleOrientationChange();
            }, 100);
        });

        // Display initial controls notification
        this.showDOMNotification('Controls: S(crouch) A/D(move) J(jump) K(dash) E(exit when available) | Hold S+J to fall through one-way platforms', 8000, {
            top: '10%',
            color: '#FFFFFF',
            fontSize: '16px',
            backgroundColor: 'rgba(0, 0, 0, 0.7)'
        });
    }



    // ===== DASH SYSTEM =====
    initDashSystem() {
        this.dash = {
            isDashing: false,
            dashSpeed: 600, // High speed for dash
            dashDuration: 200, // Dash lasts 200ms
            dashCooldown: 1000, // 1 second cooldown between dashes
            lastDashTime: 0,
            dashDirection: 0, // -1 for left, 1 for right, 0 for no direction
            lastMoveDirection: 1 // Track last movement direction (default right)
        };
    }

    updateDashSystem() {
        const currentTime = this.time.now;
        const hub = this._hub;
        const dz = 0.3;
        
        // Track movement direction when not dashing
        if (!this.dash.isDashing) {
            if (this.cursors.A.isDown || this.arrowKeys.left.isDown || (hub && hub.direction.x < -dz)) {
                this.dash.lastMoveDirection = -1;
            } else if (this.cursors.D.isDown || this.arrowKeys.right.isDown || (hub && hub.direction.x > dz)) {
                this.dash.lastMoveDirection = 1;
            }
        }
        
        // Handle dash input (K key, hub X button, or hub RB) - can't dash while crouching
        const dashDown = this.cursors.K.isDown || (hub && (hub.buttons.x || hub.buttons.rb));
        if (dashDown && !this.dash.isDashing && !this.crouch.isCrouching &&
            currentTime - this.dash.lastDashTime > this.dash.dashCooldown) {
            this.startDash();
        }
        
        // Handle dash end
        if (this.dash.isDashing && 
            currentTime - this.dash.lastDashTime > this.dash.dashDuration) {
            this.endDash();
        }
    }

    startDash() {
        this.dash.isDashing = true;
        this.dash.dashDirection = this.dash.lastMoveDirection;
        this.dash.lastDashTime = this.time.now;
        
        // Apply dash velocity
        this.player.setVelocityX(this.dash.dashDirection * this.dash.dashSpeed);
        
        // Visual effects - just use transparency without tint
        this.player.setAlpha(0.5); // Very transparent for dash effect
        
        // Create dash trail effect
        this.createDashTrail();
        
        // Show dash notification
        const direction = this.dash.dashDirection > 0 ? 'RIGHT' : 'LEFT';
        this.showDOMNotification(`DASH ${direction}!`, 800, {
            top: '20%',
            color: '#00FFFF',
            fontSize: '18px',
            backgroundColor: 'rgba(0, 255, 255, 0.3)'
        });
    }

    endDash() {
        this.dash.isDashing = false;
        
        // Restore normal appearance - use texture instead of tint
        this.player.setTexture('player_normal'); // Back to normal texture
        this.player.setAlpha(1.0); // Restore full opacity
        
        // Reduce velocity gradually instead of stopping abruptly
        const currentVelocityX = this.player.body.velocity.x;
        this.player.setVelocityX(currentVelocityX * 0.3);
    }

    createDashTrail() {
        // Create particle trail effect during dash - use white base for visibility
        const particles = this.add.particles(this.player.x, this.player.y, 'player_base', {
            quantity: 3,
            scale: { start: 0.2, end: 0 },
            speed: { min: 20, max: 50 },
            lifespan: 300,
            alpha: { start: 0.8, end: 0 }
        });
        
        // Follow player during dash
        const trailDuration = this.dash.dashDuration;
        const updateTrail = () => {
            if (this.dash.isDashing && particles.active) {
                particles.setPosition(this.player.x, this.player.y);
            }
        };
        
        // Update trail position during dash
        const trailTimer = this.time.addEvent({
            delay: 16, // ~60fps
            callback: updateTrail,
            repeat: Math.floor(trailDuration / 16)
        });
        
        // Clean up particles after dash
        this.time.delayedCall(trailDuration + 300, () => {
            if (particles && particles.active) {
                particles.destroy();
            }
        });
    }

    // ===== CROUCH SYSTEM =====
    initCrouchSystem() {
        this.crouch = {
            isCrouching: false,
            normalHeight: 48,  // Normal player height
            crouchHeight: 24,  // Half height when crouching
            normalWidth: 32    // Width stays the same
        };
        
        // Flag to track fall-through state
        this.fallingThroughOneWay = false;
    }

    updateCrouchSystem() {
        const hub = this._hub;
        const crouchDown = this.cursors.S.isDown || (hub && hub.direction.y > 0.5);
        const jumpDown  = this.cursors.J.isDown || this.arrowKeys.up.isDown || (hub && hub.buttons.a);

        // Handle crouch input (S key or hub D-pad down) - can't crouch while dashing
        if (crouchDown && !this.crouch.isCrouching && !this.dash.isDashing) {
            this.startCrouch();
        }
        
        // Handle fall-through input (crouch + jump pressed together)
        if (crouchDown && jumpDown && 
            !this.fallingThroughOneWay && this.crouch.isCrouching) {
            this.tryFallThroughOneWayPlatform();
        }
        
        // Handle crouch release (only if not falling through)
        if (!crouchDown && this.crouch.isCrouching && !this.fallingThroughOneWay) {
            this.endCrouch();
        }
        
        // Auto-release crouch if dashing starts
        if (this.dash.isDashing && this.crouch.isCrouching) {
            this.endCrouch();
        }
    }

    tryFallThroughOneWayPlatform() {
        // Avoid multiple triggers
        if (this.fallingThroughOneWay) {
            return;
        }
        
        // Check if player is standing on a one-way platform
        const playerBounds = this.player.getBounds();
        const playerBottom = playerBounds.bottom;
        
        // Check all one-way platforms
        let foundPlatform = false;
        this.platformManager.oneWayPlatforms.children.entries.forEach(platform => {
            const platformBounds = platform.getBounds();
            const platformTop = platformBounds.top;
            
            // Check if player is on top of this one-way platform
            if (playerBottom >= platformTop - 8 && playerBottom <= platformTop + 8 &&
                playerBounds.left < platformBounds.right && playerBounds.right > platformBounds.left) {
                
                foundPlatform = true;
                
                // Set falling through flag - this will be checked in oneWayCollisionCheck
                this.fallingThroughOneWay = true;
                
                // Push player down to start falling
                this.player.setVelocityY(150);
                
                // Show fall-through notification
                this.showDOMNotification('Falling Through One-Way Platform!', 800, {
                    top: '25%',
                    color: '#00AAFF',
                    fontSize: '16px',
                    backgroundColor: 'rgba(0, 170, 255, 0.3)'
                });
                
                // Re-enable collision and end crouch after player has fallen through
                this.time.delayedCall(600, () => {
                    this.fallingThroughOneWay = false;
                    // End crouch after fall-through is complete
                    this.endCrouch();
                });
                return;
            }
        });
        
        // If not on a one-way platform, show message
        if (!foundPlatform) {
            this.showDOMNotification('Not on a one-way platform!', 800, {
                top: '25%',
                color: '#FF6666',
                fontSize: '14px',
                backgroundColor: 'rgba(255, 102, 102, 0.3)'
            });
        }
    }

        startCrouch() {
        this.crouch.isCrouching = true;
        
        // Change to orange crouching texture
        this.player.setTexture('player_crouch');
        this.player.setAlpha(0.8); // Slightly transparent to show different state
    }

    endCrouch() {
        this.crouch.isCrouching = false;
        
        // Restore normal pink player texture
        this.player.setTexture('player_normal');
        this.player.setAlpha(1.0); // Full opacity
    }

    toggleGamePause() {
        if (!this.pauseManager) {
            console.warn('[GameScene] PauseManager not initialized');
            return;
        }

        // Debounce to prevent double-trigger when resuming via gamepad Start
        const now = Date.now();
        if (now - (this._lastPauseToggle || 0) < 300) return;
        this._lastPauseToggle = now;

        this.pauseManager.toggle();
    }

    createPauseOverlay() {
        if (!this.pauseOverlayManager) {
            this.pauseOverlayManager = new PauseOverlay();
        }

        this.pauseOverlayManager.show({
            id: 'game-pause-overlay',
            title: 'PAUSED',
            zIndex: 9999,
            background: 'rgba(0, 0, 0, 0.8)',
            options: [
                { id: 'resume', label: 'RESUME' },
                {
                    id: 'hub',
                    label: 'HUB',
                    onSelect: () => {
                        window.location.href = 'https://www.dreamdealer.dev';
                    }
                }
            ],
            selectedIndex: 0,
            onResume: () => {
                console.log('[GameScene] Resume requested from pause overlay');
                this.toggleGamePause();
            }
        });
    }

    removePauseOverlay() {
        if (this.pauseOverlayManager) {
            this.pauseOverlayManager.hide();
        }
    }

    // ===== BLACK HOLE SYSTEM =====
    initBlackHoleSystem() {
        this.blackHole = {
            entryHole: null,
            exitHole: null,
            entryParticles: null,
            exitParticles: null,
            isEntryAnimationActive: false,
            isExitHoleActive: false,
            playerStartX: 100,
            playerStartY: 450,
            levelCompleted: false
        };
        
        // Start entry animation when level begins
        this.time.delayedCall(500, () => {
            this.startEntryAnimation();
        });
    }

    startEntryAnimation() {
        this.blackHole.isEntryAnimationActive = true;
        
        // Create black hole at player spawn point
        this.blackHole.entryHole = this.add.container(this.blackHole.playerStartX, this.blackHole.playerStartY);
        
        // Add black hole rim
        const rim = this.add.sprite(0, 0, 'blackhole_rim');
        rim.setScale(0.1); // Start very small
        
        // Add black hole center
        const center = this.add.sprite(0, 0, 'blackhole_center');
        center.setScale(0.1); // Start very small
        
        this.blackHole.entryHole.add([rim, center]);
        
        // Create swirling particles around the hole
        this.blackHole.entryParticles = this.add.particles(this.blackHole.playerStartX, this.blackHole.playerStartY, 'blackhole_particle', {
            quantity: 2,
            scale: { start: 0.5, end: 0 },
            speed: { min: 30, max: 80 },
            lifespan: 1000,
            alpha: { start: 0.8, end: 0 },
            frequency: 50,
            emitZone: { 
                type: 'edge', 
                source: new Phaser.Geom.Circle(0, 0, 30),
                quantity: 8
            }
        });
        
        // Hide player initially
        this.player.setVisible(false);
        this.player.setVelocity(0, 0);
        this.player.body.setEnable(false); // Disable physics during animation
        
        // Scale up the black hole
        this.tweens.add({
            targets: [rim, center],
            scaleX: 2.0,
            scaleY: 2.0,
            duration: 1000,
            ease: 'Power2',
            onComplete: () => {
                // Player jumps out animation
                this.playerJumpOut();
            }
        });
        
        // Show entry notification
        this.showDOMNotification('Entering Level...', 2000, {
            top: '15%',
            color: '#8A2BE2',
            fontSize: '20px',
            backgroundColor: 'rgba(138, 43, 226, 0.3)'
        });
    }

    playerJumpOut() {
        // Position player at hole center
        this.player.setPosition(this.blackHole.playerStartX, this.blackHole.playerStartY);
        this.player.setVisible(true);
        this.player.setScale(0.5); // Start small
        
        // Jump out animation
        this.tweens.add({
            targets: this.player,
            scaleX: 1.0,
            scaleY: 1.0,
            y: this.blackHole.playerStartY - 60,
            duration: 600,
            ease: 'Back.easeOut',
            onComplete: () => {
                // Re-enable player physics and control
                this.player.body.setEnable(true);
                this.player.setVelocityY(-200); // Small bounce
                
                // Scale down and remove entry hole
                this.closeEntryHole();
            }
        });
    }

    closeEntryHole() {
        // Scale down hole
        this.tweens.add({
            targets: this.blackHole.entryHole.list,
            scaleX: 0,
            scaleY: 0,
            duration: 800,
            ease: 'Power2',
            onComplete: () => {
                this.blackHole.entryHole.destroy();
                this.blackHole.entryHole = null;
                this.blackHole.isEntryAnimationActive = false;
            }
        });
        
        // Fade out particles
        this.tweens.add({
            targets: this.blackHole.entryParticles,
            alpha: 0,
            duration: 800,
            onComplete: () => {
                this.blackHole.entryParticles.destroy();
                this.blackHole.entryParticles = null;
            }
        });
        
        // Start checking for level completion
        this.startLevelCompletionCheck();
    }

    startLevelCompletionCheck() {
        // Check every 2 seconds if level objectives are completed
        this.levelCompletionTimer = this.time.addEvent({
            delay: 2000,
            callback: this.checkLevelCompletion,
            callbackScope: this,
            loop: true
        });
    }

    checkLevelCompletion() {
        // Level completion criteria: collect at least 20 items and defeat 3 enemies
        const collectedItems = 34 - this.collectibles.children.entries.length; // Total items - remaining items
        const defeatedEnemies = 6 - this.enemies.children.entries.length; // Total enemies - remaining enemies
        
        if (collectedItems >= 20 && defeatedEnemies >= 3 && !this.blackHole.levelCompleted) {
            this.blackHole.levelCompleted = true;
            this.activateExitHole();
            
            // Stop completion checking
            if (this.levelCompletionTimer) {
                this.levelCompletionTimer.destroy();
            }
        }
    }

    activateExitHole() {
        if (this.blackHole.isExitHoleActive) return;
        
        this.blackHole.isExitHoleActive = true;
        
        // Create exit hole at the same spawn location
        this.blackHole.exitHole = this.add.container(this.blackHole.playerStartX, this.blackHole.playerStartY);
        
        // Add black hole rim
        const rim = this.add.sprite(0, 0, 'blackhole_rim');
        rim.setScale(0.1);
        
        // Add black hole center
        const center = this.add.sprite(0, 0, 'blackhole_center');
        center.setScale(0.1);
        
        this.blackHole.exitHole.add([rim, center]);
        
        // Create exit particles
        this.blackHole.exitParticles = this.add.particles(this.blackHole.playerStartX, this.blackHole.playerStartY, 'blackhole_particle', {
            quantity: 3,
            scale: { start: 0.6, end: 0 },
            speed: { min: 40, max: 100 },
            lifespan: 1200,
            alpha: { start: 1.0, end: 0 },
            frequency: 40,
            emitZone: { 
                type: 'edge', 
                source: new Phaser.Geom.Circle(0, 0, 40),
                quantity: 10
            }
        });
        
        // Scale up the exit hole
        this.tweens.add({
            targets: [rim, center],
            scaleX: 2.0,
            scaleY: 2.0,
            duration: 1200,
            ease: 'Power2',
            yoyo: true,
            repeat: -1, // Pulse infinitely
            repeatDelay: 500
        });
        
        // Show completion notification
        this.showDOMNotification('Level Complete! Return to the black hole to exit (Press E near hole)', 5000, {
            top: '10%',
            color: '#00FF00',
            fontSize: '18px',
            backgroundColor: 'rgba(0, 255, 0, 0.3)'
        });
    }

    updateBlackHoleSystem() {
        const hub = this._hub;
        const exitDown = (this.cursors.E && this.cursors.E.isDown) || (hub && hub.buttons.y);

        // Check if player is near exit hole and wants to exit (E key or hub Y button)
        if (this.blackHole.isExitHoleActive && this.blackHole.exitHole && exitDown) {
            const distance = Phaser.Math.Distance.Between(
                this.player.x, this.player.y,
                this.blackHole.exitHole.x, this.blackHole.exitHole.y
            );
            
            if (distance < 80) {
                this.exitLevel();
            }
        }
    }

    exitLevel() {
        if (this.blackHole.isEntryAnimationActive) return;
        
        // Show exit message
        this.showDOMNotification('Exiting Level... Level Complete!', 3000, {
            top: '20%',
            color: '#FFD700',
            fontSize: '24px',
            backgroundColor: 'rgba(255, 215, 0, 0.4)'
        });
        
        // Disable player control
        this.player.body.setEnable(false);
        
        // Suck player into hole animation
        this.tweens.add({
            targets: this.player,
            x: this.blackHole.exitHole.x,
            y: this.blackHole.exitHole.y,
            scaleX: 0.3,
            scaleY: 0.3,
            alpha: 0.5,
            duration: 1000,
            ease: 'Power2',
            onComplete: () => {
                this.player.setVisible(false);
                // Could restart scene or load next level here
                this.time.delayedCall(2000, () => {
                    this.scene.restart(); // Restart the current scene
                });
            }
        });
        
        // Scale up exit hole for dramatic effect
        this.tweens.add({
            targets: this.blackHole.exitHole.list,
            scaleX: 3.0,
            scaleY: 3.0,
            duration: 1000,
            ease: 'Power2'
        });
    }

    update() {
        if (this.gameOver) {
            return;
        }

        // Cache hub controls state for this frame (Layer 1: touch D-pad + gamepad)
        const hub = (typeof HubControls !== 'undefined' && HubControls.enabled)
            ? HubControls.getState() : null;
        this._hub = hub;
        const dz = 0.3;

        // Pause toggle via hub Start button (Layer 2: game decides what "start" means)
        if (typeof HubControls !== 'undefined' && HubControls.justPressed('start')) {
            this.toggleGamePause();
            return;
        }

        // Update dash system
        this.updateDashSystem();
        
        // Update crouch system
        this.updateCrouchSystem();
        
        // Update black hole system
        this.updateBlackHoleSystem();

        // Player movement — keyboard + hub controls (touch D-pad / gamepad)
        const baseSpeed = 160;
        const jumpSpeed = 500;
        const speedMultiplier = this.mobileSpeedMultiplier || 1;

        const leftDown  = this.arrowKeys.left.isDown  || this.cursors.A.isDown || (hub && hub.direction.x < -dz);
        const rightDown = this.arrowKeys.right.isDown || this.cursors.D.isDown || (hub && hub.direction.x >  dz);
        const jumpDown  = this.arrowKeys.up.isDown    || this.cursors.J.isDown || (hub && hub.buttons.a);

        // Left movement - only if not dashing
        if (leftDown && !this.dash.isDashing) {
            const moveSpeed = this.crouch.isCrouching ? baseSpeed * 0.6 : baseSpeed;
            this.player.setVelocityX(-moveSpeed * speedMultiplier);
        }
        // Right movement - only if not dashing
        else if (rightDown && !this.dash.isDashing) {
            const moveSpeed = this.crouch.isCrouching ? baseSpeed * 0.6 : baseSpeed;
            this.player.setVelocityX(moveSpeed * speedMultiplier);
        }
        else if (!this.dash.isDashing) {
            this.player.setVelocityX(0);
        }

        // Regular jump - can't jump while crouching OR falling through
        if (jumpDown && 
            this.player.body.touching.down && !this.crouch.isCrouching && !this.fallingThroughOneWay) {
            this.player.setVelocityY(-jumpSpeed);
        }

        // Check if player falls off the expanded world
        if (this.player.y > 1340) {
            this.playerDeath();
        }

        // Check for section changes and show DOM notifications
        this.checkPlayerSection();
    }

    collectItem(player, item) {
        item.disableBody(true, true);
        this.score += 10;
        this.updateDOMHUD();

        // Show DOM-based score notification
        this.showDOMNotification('+10 Points!', 1000, {
            top: '30%',
            color: '#FFD700',
            fontSize: window.innerWidth < window.innerHeight ? '18px' : '22px'
        });

        if (this.collectibles.countActive(true) === 0) {
            // Show DOM-based level complete notification
            this.showDOMNotification('All Items Collected!<br/>Enemies Speed Increased!', 3000, {
                backgroundColor: 'rgba(255, 165, 0, 0.9)',
                color: '#fff',
                fontSize: window.innerWidth < window.innerHeight ? '20px' : '26px'
            });

            // Re-enable all collectibles
            this.collectibles.children.entries.forEach((child) => {
                child.enableBody(true, child.x, 0, true, true);
            });

            // Increase enemy speed
            this.enemies.children.entries.forEach((enemy) => {
                enemy.setVelocityX(enemy.body.velocity.x * 1.2);
            });
        }
    }

    hitEnemy(player, enemy) {
        // If player is dashing, destroy the enemy instead of dying
        if (this.dash && this.dash.isDashing) {
            this.destroyEnemy(enemy);
            return;
        }
        
        // Normal collision - player dies
        this.playerDeath();
    }
    
    destroyEnemy(enemy) {
        // Create destruction effect
        this.add.particles(enemy.x, enemy.y, 'enemy_red', {
            quantity: 12,
            scale: { start: 0.4, end: 0 },
            speed: { min: 80, max: 200 },
            lifespan: 600
        });
        
        // Show destruction notification
        this.showDOMNotification('Enemy Destroyed!', 1000, {
            top: '15%',
            color: '#FF4444',
            fontSize: '16px',
            backgroundColor: 'rgba(255, 68, 68, 0.3)'
        });
        
        // Add points for destroying enemy
        this.score += 50;
        this.updateDOMHUD();
        
        // Remove enemy
        enemy.destroy();
    }

    playerDeath() {
        this.lives -= 1;
        this.updateDOMHUD();

        if (this.lives <= 0) {
            this.physics.pause();
            this.player.setAlpha(0.5); // Gray out by reducing opacity instead of tinting
            this.gameOver = true;
            
            // Create DOM-based game over screen
            this.createGameOverScreen();
        } else {
            // Show DOM-based death notification
            this.showDOMNotification(`Life Lost!<br/>Lives Remaining: ${this.lives}`, 2000, {
                backgroundColor: 'rgba(220, 20, 60, 0.9)',
                color: '#fff',
                fontSize: window.innerWidth < window.innerHeight ? '20px' : '24px'
            });

            // Respawn player in Section 3 (top-left) 
            this.player.setPosition(100, 450);
            this.player.setVelocity(0, 0);
            
            // Reset player appearance and state
            this.player.setTexture('player_normal'); // Normal pink player texture
            this.player.setAlpha(1.0); // Full opacity
            
            // Reset crouch state
            this.crouch.isCrouching = false;
            
            // Reset fall-through state
            this.fallingThroughOneWay = false;
        }
    }

    setupOrientationGameplay() {
        // *** ORIENTATION DETECTION ***
        // When viewport WIDTH < HEIGHT = VERTICAL/PORTRAIT mode
        // When viewport WIDTH > HEIGHT = HORIZONTAL/LANDSCAPE mode
        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;
        const isVerticalMode = viewportWidth < viewportHeight;
        const isHorizontalMode = viewportWidth > viewportHeight;
        
        console.log(`Viewport: ${viewportWidth}x${viewportHeight}, Mode: ${isVerticalMode ? 'VERTICAL' : 'HORIZONTAL'}`);
        
        // Resize the game canvas to match viewport
        this.scale.resize(viewportWidth, viewportHeight);
        
        // *** UNIVERSAL CAMERA SYSTEM - SAME BEHAVIOR FOR ALL MODES ***
        const gameWidth = 2048;
        const gameHeight = 1280;
        
        console.log(`Mode: ${isVerticalMode ? 'VERTICAL' : 'HORIZONTAL'}, viewport=${viewportWidth}x${viewportHeight}`);
        
        // Calculate optimal zoom based on viewport
        let zoomLevel;
        if (isVerticalMode && this.isMobile) {
            // Vertical mobile: prioritize fitting height, allow horizontal scrolling
            zoomLevel = Math.min(viewportHeight / 640, viewportWidth / 320); // Zoom in more for mobile vertical
            this.mobileSpeedMultiplier = 0.8;
            this.updateDOMHUDForVertical();
        } else {
            // Horizontal (mobile landscape + desktop): standard following camera
            zoomLevel = Math.min(viewportWidth / 1024, viewportHeight / 640); // Comfortable zoom level
            this.mobileSpeedMultiplier = 1;
            this.updateDOMHUDForHorizontal();
        }
        
        console.log(`Zoom level: ${zoomLevel.toFixed(2)}`);
        
        // Apply zoom and setup camera following for ALL modes
        this.cameras.main.setZoom(zoomLevel);
        
        // UNIVERSAL camera following - works the same for all orientations
        this.cameras.main.startFollow(this.player, true, 0.06, 0.06);
        this.cameras.main.setFollowOffset(0, 0);
        
        // Set camera bounds to full world - NO INVISIBLE WALLS
        this.cameras.main.setBounds(0, 0, gameWidth, gameHeight);
        
        // Responsive deadzone based on zoom level
        const deadzoneX = Math.max(40, 80 / zoomLevel);
        const deadzoneY = Math.max(30, 60 / zoomLevel);
        this.cameras.main.setDeadzone(deadzoneX, deadzoneY);
        
        // Store current orientation for comparison
        this.isPortrait = isVerticalMode;
    }

    handleOrientationChange() {
        if (this.gameOver) return;
        
        // Check if orientation actually changed
        const newIsVertical = window.innerWidth < window.innerHeight;
        const orientationChanged = newIsVertical !== this.isPortrait;
        
        if (orientationChanged) {
            console.log(`Orientation changed to: ${newIsVertical ? 'VERTICAL' : 'HORIZONTAL'}`);
            
            // FORCE camera reset before setting up new orientation
            this.cameras.main.stopFollow();
            this.cameras.main.setZoom(1);
            this.cameras.main.setDeadzone(0, 0);
            this.cameras.main.setBounds();
            
            // Setup new orientation
            this.setupOrientationGameplay();
            
            // Add DOM-based visual feedback for orientation change
            this.showOrientationNotification(newIsVertical);
        }
    }

    createDOMHUD() {
        // Create DOM elements for HUD that overlay the game canvas
        const gameContainer = document.getElementById('game-container');
        
        // Create HUD container
        this.hudContainer = document.createElement('div');
        this.hudContainer.id = 'hud-container';
        this.hudContainer.style.cssText = `
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            pointer-events: none;
            z-index: 1000;
            font-family: Arial, sans-serif;
            font-weight: bold;
        `;
        
        // Create Score element
        this.scoreElement = document.createElement('div');
        this.scoreElement.id = 'score-hud';
        this.scoreElement.style.cssText = `
            position: absolute;
            top: 20px;
            left: 20px;
            font-size: 24px;
            color: #000;
            text-shadow: 2px 2px 4px rgba(255,255,255,0.8);
        `;
        this.scoreElement.textContent = 'Score: 0';
        
        // Create Lives element
        this.livesElement = document.createElement('div');
        this.livesElement.id = 'lives-hud';
        this.livesElement.style.cssText = `
            position: absolute;
            top: 60px;
            left: 20px;
            font-size: 24px;
            color: #000;
            text-shadow: 2px 2px 4px rgba(255,255,255,0.8);
        `;
        this.livesElement.textContent = 'Lives: 3';
        
        // Add elements to HUD container
        this.hudContainer.appendChild(this.scoreElement);
        this.hudContainer.appendChild(this.livesElement);
        
        // Add HUD container to game container
        gameContainer.appendChild(this.hudContainer);
        
        console.log('DOM HUD created');
    }

    updateDOMHUD() {
        // Update DOM HUD elements with current values
        if (this.scoreElement) {
            this.scoreElement.textContent = `Score: ${this.score}`;
        }
        if (this.livesElement) {
            this.livesElement.textContent = `Lives: ${this.lives}`;
        }
    }

    updateDOMHUDForVertical() {
        // Scale HUD for vertical mode
        if (this.scoreElement && this.livesElement) {
            const scaleFactor = Math.max(1.2, window.innerHeight / 640);
            const fontSize = Math.max(28, 24 * scaleFactor);
            
            this.scoreElement.style.fontSize = `${fontSize}px`;
            this.livesElement.style.fontSize = `${fontSize}px`;
            
            console.log(`DOM HUD scaled for vertical: ${fontSize}px font`);
        }
    }

    updateDOMHUDForHorizontal() {
        // Reset HUD for horizontal mode
        if (this.scoreElement && this.livesElement) {
            this.scoreElement.style.fontSize = '24px';
            this.livesElement.style.fontSize = '24px';
            
            console.log('DOM HUD reset for horizontal');
        }
    }

    createGameOverScreen() {
        // Create DOM-based game over screen
        const gameContainer = document.getElementById('game-container');
        
        this.gameOverContainer = document.createElement('div');
        this.gameOverContainer.id = 'game-over-container';
        this.gameOverContainer.style.cssText = `
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            background-color: rgba(0, 0, 0, 0.5);
            display: flex;
            flex-direction: column;
            justify-content: center;
            align-items: center;
            z-index: 2000;
            font-family: Arial, sans-serif;
            text-align: center;
        `;
        
        // Game Over title
        const gameOverTitle = document.createElement('h1');
        gameOverTitle.style.cssText = `
            font-size: ${window.innerWidth < window.innerHeight ? '48px' : '64px'};
            color: #ff0000;
            margin: 0 0 20px 0;
            text-shadow: 2px 2px 4px rgba(0,0,0,0.8);
        `;
        gameOverTitle.textContent = 'GAME OVER';
        
        // Restart instruction
        const restartText = document.createElement('p');
        restartText.style.cssText = `
            font-size: ${window.innerWidth < window.innerHeight ? '24px' : '32px'};
            color: #fff;
            margin: 0;
            text-shadow: 1px 1px 2px rgba(0,0,0,0.8);
        `;
        restartText.textContent = 'Press F5 to restart';
        
        this.gameOverContainer.appendChild(gameOverTitle);
        this.gameOverContainer.appendChild(restartText);
        gameContainer.appendChild(this.gameOverContainer);
        
        console.log('DOM Game Over screen created');
    }

    showOrientationNotification(isVertical) {
        // Create DOM-based orientation change notification
        const gameContainer = document.getElementById('game-container');
        
        const notification = document.createElement('div');
        notification.id = 'orientation-notification';
        notification.style.cssText = `
            position: absolute;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);
            background-color: rgba(0, 0, 0, 0.8);
            color: #ffffff;
            font-family: Arial, sans-serif;
            font-weight: bold;
            font-size: ${window.innerWidth < window.innerHeight ? '24px' : '32px'};
            padding: 20px 30px;
            border-radius: 8px;
            text-align: center;
            z-index: 1500;
            text-shadow: 1px 1px 2px rgba(0,0,0,0.8);
            box-shadow: 0 4px 16px rgba(0,0,0,0.5);
        `;
        
        notification.innerHTML = isVertical ? 
            'VERTICAL MODE<br/>Camera Following!' : 
            'HORIZONTAL MODE<br/>Camera Following!';
        
        gameContainer.appendChild(notification);
        
        // Fade out notification after 1.5 seconds
        setTimeout(() => {
            notification.style.transition = 'opacity 0.5s ease-out';
            notification.style.opacity = '0';
            
            setTimeout(() => {
                if (notification.parentNode) {
                    notification.parentNode.removeChild(notification);
                }
            }, 500);
        }, 1500);
        
        console.log(`DOM orientation notification shown: ${isVertical ? 'VERTICAL' : 'HORIZONTAL'}`);
    }

    showDOMNotification(message, duration = 2000, style = {}) {
        // General purpose DOM notification system
        const gameContainer = document.getElementById('game-container');
        
        const notification = document.createElement('div');
        notification.className = 'game-notification';
        
        const defaultStyle = {
            position: 'absolute',
            top: '20%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            color: '#ffffff',
            fontFamily: 'Arial, sans-serif',
            fontWeight: 'bold',
            fontSize: window.innerWidth < window.innerHeight ? '20px' : '24px',
            padding: '15px 25px',
            borderRadius: '8px',
            textAlign: 'center',
            zIndex: '1500',
            textShadow: '1px 1px 2px rgba(0,0,0,0.8)',
            boxShadow: '0 4px 16px rgba(0,0,0,0.5)'
        };
        
        // Apply styles
        Object.assign(notification.style, defaultStyle, style);
        notification.innerHTML = message;
        
        gameContainer.appendChild(notification);
        
        // Fade out notification
        setTimeout(() => {
            notification.style.transition = 'opacity 0.5s ease-out';
            notification.style.opacity = '0';
            
            setTimeout(() => {
                if (notification.parentNode) {
                    notification.parentNode.removeChild(notification);
                }
            }, 500);
        }, duration);
        
        return notification;
    }

    checkPlayerSection() {
        // Track which section the player is currently in and show notifications
        const playerX = this.player.x;
        const playerY = this.player.y;
        
        let currentSection = '';
        
        if (playerX < 1024 && playerY < 640) {
            currentSection = 'section3'; // Top-Left
        } else if (playerX >= 1024 && playerY < 640) {
            currentSection = 'section4'; // Top-Right
        } else if (playerX < 1024 && playerY >= 640) {
            currentSection = 'section1'; // Bottom-Left
        } else if (playerX >= 1024 && playerY >= 640) {
            currentSection = 'section2'; // Bottom-Right
        }
        
        // Show notification only when entering a new section
        if (currentSection !== this.lastSection && currentSection !== '') {
            const sectionNames = {
                'section1': 'Bottom-Left Area',
                'section2': 'Bottom-Right Area', 
                'section3': 'Top-Left Area',
                'section4': 'Top-Right Area'
            };
            
            const sectionColors = {
                'section1': 'rgba(34, 139, 34, 0.9)',      // Green
                'section2': 'rgba(50, 205, 50, 0.9)',      // Lime Green
                'section3': 'rgba(46, 139, 87, 0.9)',      // Sea Green
                'section4': 'rgba(107, 142, 35, 0.9)'      // Olive Drab
            };
            
            if (this.lastSection) { // Don't show notification on initial load
                this.showDOMNotification(`Entering ${sectionNames[currentSection]}`, 2000, {
                    backgroundColor: sectionColors[currentSection],
                    color: '#fff',
                    fontSize: window.innerWidth < window.innerHeight ? '18px' : '22px',
                    top: '25%'
                });
            }
            
            this.lastSection = currentSection;
        }
    }

    cleanupDOMElements() {
        // Clean up all DOM elements when scene is destroyed
        const gameContainer = document.getElementById('game-container');
        if (gameContainer) {
            // Remove HUD
            const hudContainer = document.getElementById('hud-container');
            if (hudContainer) {
                hudContainer.remove();
            }
            
            // Remove game over screen
            const gameOverContainer = document.getElementById('game-over-container');
            if (gameOverContainer) {
                gameOverContainer.remove();
            }
            
            // Remove any remaining notifications
            const notifications = gameContainer.querySelectorAll('.game-notification, #orientation-notification');
            notifications.forEach(notification => notification.remove());
        }
    }

    createRoom1() {
        // === ROOM 1: Bottom-Left (Forest Theme) ===
        // Ground platform with holes and one-way sections
        // Left ground section - solid
        this.platformManager.createPlatform(150, 1260, 300, 40, PLATFORM_TYPES.SOLID, 0x228B22);
        // Gap (hole) from x: 300-400
        // Middle ground section - one-way (can fall through with S+J) - BRIGHT YELLOW OCHRE
        this.platformManager.createPlatform(500, 1260, 200, 40, PLATFORM_TYPES.ONE_WAY, 0xDAA520);
        // Gap (hole) from x: 600-700  
        // Right ground section - solid
        this.platformManager.createPlatform(824, 1260, 300, 40, PLATFORM_TYPES.SOLID, 0x228B22);
        
        // Stepping stones pattern - mix of solid and one-way platforms
        this.platformManager.createPlatform(150, 1140, 120, 20, PLATFORM_TYPES.SOLID, 0x228B22);
        this.platformManager.createPlatform(350, 1080, 120, 20, PLATFORM_TYPES.ONE_WAY, 0xDAA520); // One-way platform - YELLOW OCHRE
        this.platformManager.createPlatform(550, 1020, 120, 20, PLATFORM_TYPES.BREAKABLE, 0xDAA520); // Breakable platform
        this.platformManager.createPlatform(750, 960, 120, 20, PLATFORM_TYPES.SOLID, 0x228B22);
        
        // Floating islands - one-way platforms for vertical navigation
        this.platformManager.createPlatform(200, 900, 150, 25, PLATFORM_TYPES.ONE_WAY, 0xDAA520); // YELLOW OCHRE
        this.platformManager.createPlatform(600, 840, 180, 25, PLATFORM_TYPES.BREAKABLE, 0xCD853F);
        
        // Hidden secret platform - breakable for discovery
        this.platformManager.createPlatform(50, 780, 100, 15, PLATFORM_TYPES.BREAKABLE, 0xB8860B);
        
        // Bridge platforms over holes for easier navigation
        this.platformManager.createPlatform(350, 1200, 100, 15, PLATFORM_TYPES.ONE_WAY, 0xDAA520); // YELLOW OCHRE
        this.platformManager.createPlatform(650, 1200, 100, 15, PLATFORM_TYPES.ONE_WAY, 0xDAA520); // YELLOW OCHRE
    }

    createRoom2() {
        // === ROOM 2: Bottom-Right (Desert Theme) ===
        // Ground platform with holes and one-way sections
        // Left ground section - one-way (desert sand you can fall through) - YELLOW OCHRE
        this.platformManager.createPlatform(1200, 1260, 250, 40, PLATFORM_TYPES.ONE_WAY, 0xDAA520);
        // Gap (hole) from x: 1450-1550
        // Middle ground section - solid
        this.platformManager.createPlatform(1650, 1260, 200, 40, PLATFORM_TYPES.SOLID, 0x32CD32);
        // Gap (hole) from x: 1850-1900
        // Right ground section - breakable
        this.platformManager.createPlatform(1976, 1260, 144, 40, PLATFORM_TYPES.BREAKABLE, 0xDEB887);
        
        // Desert platforms with varied types
        this.platformManager.createPlatform(1200, 1140, 180, 20, PLATFORM_TYPES.ONE_WAY, 0xDAA520); // YELLOW OCHRE one-way
        this.platformManager.createPlatform(1600, 1040, 220, 20, PLATFORM_TYPES.BREAKABLE, 0xDEB887); // Breakable sand platform
        this.platformManager.createPlatform(1850, 940, 180, 20, PLATFORM_TYPES.SOLID, 0xD2B48C); // Solid tan platform
        this.platformManager.createPlatform(1100, 940, 160, 20, PLATFORM_TYPES.ONE_WAY, 0xDAA520); // YELLOW OCHRE one-way
        
        // Bridge platforms over holes - sandy bridges
        this.platformManager.createPlatform(1500, 1200, 100, 15, PLATFORM_TYPES.ONE_WAY, 0xDAA520); // YELLOW OCHRE
        this.platformManager.createPlatform(1875, 1200, 50, 15, PLATFORM_TYPES.BREAKABLE, 0xDEB887);
    }

    createRoom3() {
        // === ROOM 3: Top-Left (Mountain Theme) ===
        // Ground platform with mountain gaps and rocky sections
        // Left ground section - solid rock
        this.platformManager.createPlatform(200, 620, 300, 40, PLATFORM_TYPES.SOLID, 0x2E8B57);
        // Gap (ravine) from x: 500-600
        // Middle ground section - breakable rock ledge
        this.platformManager.createPlatform(700, 620, 200, 40, PLATFORM_TYPES.BREAKABLE, 0x8FBC8F);
        // Gap (ravine) from x: 900-950
        // Right ground section - solid
        this.platformManager.createPlatform(1000, 620, 200, 40, PLATFORM_TYPES.SOLID, 0x2E8B57);
        
        // Mountain platforms with challenging navigation
        this.platformManager.createPlatform(200, 500, 200, 20, PLATFORM_TYPES.SOLID, 0x2E8B57);
        this.platformManager.createPlatform(600, 400, 200, 20, PLATFORM_TYPES.ONE_WAY, 0xDAA520); // YELLOW OCHRE one-way for upward progression
        this.platformManager.createPlatform(850, 300, 200, 20, PLATFORM_TYPES.BREAKABLE, 0x8FBC8F); // Breakable rocky platform
        this.platformManager.createPlatform(100, 300, 150, 20, PLATFORM_TYPES.ONE_WAY, 0xDAA520); // YELLOW OCHRE one-way cliff platform
        
        // Bridge platforms over ravines - rocky bridges
        this.platformManager.createPlatform(550, 580, 100, 15, PLATFORM_TYPES.ONE_WAY, 0xDAA520); // YELLOW OCHRE
        this.platformManager.createPlatform(925, 580, 50, 15, PLATFORM_TYPES.BREAKABLE, 0x8FBC8F);
    }

    createRoom4() {
        // === ROOM 4: Top-Right (Industrial Theme) ===
        // Ground platform with industrial gaps and maintenance sections
        // Left ground section - breakable grating
        this.platformManager.createPlatform(1200, 620, 250, 40, PLATFORM_TYPES.BREAKABLE, 0x808080);
        // Gap (maintenance shaft) from x: 1450-1600
        // Middle ground section - one-way conveyor belt - YELLOW OCHRE
        this.platformManager.createPlatform(1700, 620, 200, 40, PLATFORM_TYPES.ONE_WAY, 0xDAA520);
        // Gap (pit) from x: 1900-1950
        // Right ground section - solid steel
        this.platformManager.createPlatform(2000, 620, 100, 40, PLATFORM_TYPES.SOLID, 0x6B8E23);
        
        // Industrial platforms with mixed mechanics
        this.platformManager.createPlatform(1200, 500, 200, 20, PLATFORM_TYPES.BREAKABLE, 0x808080); // Breakable metal platform
        this.platformManager.createPlatform(1600, 400, 200, 20, PLATFORM_TYPES.ONE_WAY, 0xDAA520); // YELLOW OCHRE one-way conveyor-like platform
        this.platformManager.createPlatform(1850, 300, 200, 20, PLATFORM_TYPES.SOLID, 0x6B8E23); // Solid steel platform
        this.platformManager.createPlatform(1100, 300, 150, 20, PLATFORM_TYPES.BREAKABLE, 0x696969); // Breakable maintenance platform
        
        // Bridge platforms over gaps - industrial catwalks
        this.platformManager.createPlatform(1525, 580, 150, 15, PLATFORM_TYPES.ONE_WAY, 0xDAA520); // YELLOW OCHRE
        this.platformManager.createPlatform(1925, 580, 50, 15, PLATFORM_TYPES.BREAKABLE, 0x808080);
    }

    createRoomConnections() {
        // === UNIQUE PASSAGEWAYS BETWEEN ROOMS ===
        
        // 1. Spiral Bridge: Room 1 to Room 3 (Bottom-Left to Top-Left) - Mixed platform types
        this.platformManager.createPlatform(450, 800, 80, 15, PLATFORM_TYPES.ONE_WAY, 0xFF6347);
        this.platformManager.createPlatform(520, 780, 80, 15, PLATFORM_TYPES.BREAKABLE, 0xFF4500);
        this.platformManager.createPlatform(590, 760, 80, 15, PLATFORM_TYPES.ONE_WAY, 0xFF6347);
        this.platformManager.createPlatform(660, 740, 80, 15, PLATFORM_TYPES.SOLID, 0xFF6347);
        this.platformManager.createPlatform(730, 720, 80, 15, PLATFORM_TYPES.ONE_WAY, 0xFF6347);
        
        // 2. Elevator Platform: Room 2 to Room 4 (Bottom-Right to Top-Right) - One-way for upward flow
        this.platformManager.createPlatform(1580, 800, 60, 15, PLATFORM_TYPES.ONE_WAY, 0xFF6347);
        this.platformManager.createPlatform(1580, 750, 60, 15, PLATFORM_TYPES.ONE_WAY, 0xFF6347);
        this.platformManager.createPlatform(1580, 700, 60, 15, PLATFORM_TYPES.ONE_WAY, 0xFF6347);
        
        // 3. Jumping Challenge: Room 1 to Room 2 (Horizontal bridge) - Challenging breakable platforms
        this.platformManager.createPlatform(900, 1100, 60, 20, PLATFORM_TYPES.BREAKABLE, 0xFF6347);
        this.platformManager.createPlatform(1024, 1050, 80, 20, PLATFORM_TYPES.SOLID, 0xFF4500); // Central solid platform
        this.platformManager.createPlatform(1148, 1100, 60, 20, PLATFORM_TYPES.BREAKABLE, 0xFF6347);
        
        // 4. Secret Tunnel: Room 3 to Room 4 (Top horizontal connection) - Mixed types for variety
        this.platformManager.createPlatform(850, 460, 60, 20, PLATFORM_TYPES.ONE_WAY, 0xFF6347);
        this.platformManager.createPlatform(1024, 440, 100, 20, PLATFORM_TYPES.BREAKABLE, 0xFF4500); // Challenging central platform
        this.platformManager.createPlatform(1200, 460, 60, 20, PLATFORM_TYPES.ONE_WAY, 0xFF6347);
        
        // 5. Central Hub Platform (connects all rooms) - Solid central meeting point
        this.platformManager.createPlatform(1024, 640, 150, 30, PLATFORM_TYPES.SOLID, 0xFF4500); // Special orange color
    }
} 